import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Column,
  MeatballMenu,
  MeatballMenuItem,
  SecondaryGrayButton,
  Severity,
  StatusType,
  TableBase,
  Title,
  Tooltip
} from "@velaro/velaro-component-lib";
import SearchBar from "../../../components/SearchBar";
import EditUserPanel from "./EditUserPanel";
import useRoleListItems from "@/hooks/useRoleListItems";
import IconDeactivateUserPerson from "@/icons/IconDeactivateUserPerson";
import DeactivateUserModal from "./DeactivateUserModal";
import IconEditPencil from "@/icons/IconEditPencil";
import IconContactsCrmSmall from "@/icons/IconContactsCrmSmall";
import { useNavigate } from "react-router-dom";
import useDeactivate from "@/hooks/useDeactivate";
import useSkillListItems from "@/hooks/useSkillListItems";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import IconPersonAdd from "@/icons/IconPersonAdd";
import { User } from "./types";
import useTeams from "@/hooks/useTeams";
import { Avatar } from "@velaro/velaro-component-lib/";
import BulkActionBar from "@/components/BulkActionBar";
import useToastAlert from "@/hooks/useToastAlert";
import { UserStatusInput } from "./UserStatusInput";
import useUsers from "@/hooks/useUsers";
import ActionBar from "../ChatWindow/ActionBar";

const columns: Column[] = [
  { label: "Display Name", accessor: "displayName" },
  { label: "Email", accessor: "email" },
  { label: "Status", accessor: "status" },
  { label: "Roles", accessor: "roles" },
  { label: "Teams", accessor: "teamIds" },
  { label: "Skills", accessor: "skillIds" },
  { label: "", accessor: "edit", disableSort: true }
];

export default function Users() {
  const { users, setUsers, fetchUsers } = useUsers();
  const [editUserId, setEditUserId] = useState<number | undefined>();
  const [deleteUserId, setDeleteUserId] = useState<number | undefined>();
  const [searchString, setSearchString] = useState("");
  const { getTeamNameById } = useTeams();
  const { getSkillNameById } = useSkillListItems();
  const { getRoleNameById } = useRoleListItems();
  const api = useApi();
  const navigate = useNavigate();
  const deactivate = useDeactivate();
  const [selectedIndicies, setSelectedIndicies] = useState<number[]>([]);
  const [bulkMaxChats, setBulkMaxChats] = useState(4);
  const { displayToast } = useToastAlert();

  async function handleDeactivate() {
    const response = await deactivate.deactivateUser(deleteUserId);

    if (response?.ok) {
      setDeleteUserId(undefined);
    } else {
      alert("Failed to delete user.");
    }

    const index = users.findIndex((x) => x.id === deleteUserId);
    const clone = [...users];
    clone.splice(index, 1);
    setUsers(clone);
  }

  const filteredUsers = useMemo(
    () =>
      users.filter((val) => {
        if (searchString === "") {
          return val;
        } else if (
          val.displayName.toLowerCase().includes(searchString.toLowerCase()) ||
          val.email.toLowerCase().includes(searchString.toLowerCase())
        ) {
          return val;
        }
      }),
    [users, searchString]
  );

  async function bulkUpdate() {
    const userIds = selectedIndicies.map((x) => filteredUsers[x].id);

    const response = await api.messaging.post("Users/Bulk", {
      userIds: userIds,
      maxChats: bulkMaxChats
    });

    if (response.ok) {
      fetchUsers();
      setSelectedIndicies([]);
      displayToast(Severity.Success, "Bulk update successful.");
    } else {
      const data = await response.json();
      displayToast(
        Severity.Error,
        "Failed to bulk update users. " + data.title
      );
    }
  }

  const tableData = useMemo(() => {
    function getMeatballItems(id: number): MeatballMenuItem[] {
      return [
        {
          icon: IconEditPencil,
          label: "Edit User",
          onClick: () => setEditUserId(id)
        },
        {
          icon: IconContactsCrmSmall,
          label: "View User Profile",
          onClick: () => navigate(`/users/${id}`)
        },
        {
          icon: IconDeactivateUserPerson,
          label: "Deactivate User",
          onClick: () => setDeleteUserId(id)
        }
      ];
    }

    function getTeams(user: User) {
      if (user.teamIds?.length === 0) {
        return (
          <>
            <span className="w-4">
              <Tooltip
                text="This user is not in any teams and will not receive any new conversations."
                direction="top"
              >
                <i className="fa fa-info-circle pr-2 text-crimson-crimson-red-500" />
              </Tooltip>
            </span>
            <span className="text-slate-500">(None)</span>
          </>
        );
      }

      const teams = user.teamIds.map((x) => getTeamNameById(x));
      return teams.join(", ");
    }

    function getSkills(user: User) {
      if (user.skillIds?.length === 0) {
        return "None";
      }
      const skills = user.skillIds.map((x) => getSkillNameById(x));
      return skills.join(", ");
    }

    function getRoles(user: User) {
      if (user.roles?.length === 0 || user.roles === null) {
        return "(None)";
      }

      const roleNames = user.roles.map((x) => {
        const parsedNumber = parseInt(x);
        if (!isNaN(parsedNumber)) {
          return getRoleNameById(parsedNumber);
        } else {
          return x;
        }
      });
      return roleNames.join(", ");
    }

    function getDisplayName(user: User) {
      return (
        <div className="flex items-center">
          <div className="w-10 h-10 relative z-0 flex-grow-0 flex-shrink-0">
            <div className="v-avatar-sq">
              <Avatar
                name={
                  user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.displayName
                }
                url={user?.picture}
                size={40}
              />
            </div>
            {user.teamIds?.length === 0 && (
              <div className="absolute inset-0 z-20 bg-white opacity-50" style={{ borderRadius: 6 }}></div>
            )}
          </div>
          <div
            className="p-4 cursor-pointer hover:underline"
            onClick={() => navigate(`/users/${user.id}`)}
          >
            {user.displayName}
          </div>
        </div>
      );
    }

    async function changeUserStatus(userId: number, status: StatusType) {
      const result = await api.messaging.post("UserStatus/admin", {
        userId: userId,
        status: status
      });

      if (!result.ok) {
        displayToast(
          Severity.Error,
          "There was a problem changing the user status."
        );
        return;
      }

      const userIndex = users.findIndex((x) => x.id === userId);
      if (userIndex === -1) {
        return;
      }
      const updatedUsers = [...users];
      updatedUsers[userIndex] = {
        ...updatedUsers[userIndex],
        status: status
      };
      setUsers(updatedUsers);
    }

    return filteredUsers.map((user) => ({
      ...user,
      displayName: getDisplayName(user),
      roles: getRoles(user),
      teamIds: getTeams(user),
      skillIds: getSkills(user),
      status: (
        <UserStatusInput
          status={user.status}
          onStatusChange={(status) => {
            changeUserStatus(user.id, status);
          }}
        />
      ),
      edit: (
        <>
          <ActionBar
            onEdit={() => {
              setEditUserId(user.id);
            }}
            onCopy={() => {}}
            onDelete={() => {
              setDeleteUserId(user.id);
            }}
          />
        </>
      )
    }));
  }, [
    api.messaging,
    displayToast,
    filteredUsers,
    getRoleNameById,
    getSkillNameById,
    getTeamNameById,
    navigate,
    setUsers,
    users
  ]);

  if (editUserId) {
    return (
      <EditUserPanel
        userId={editUserId}
        onClose={() => {
          setEditUserId(undefined);
          fetchUsers();
        }}
        onUserUpdated={() => {
          setEditUserId(undefined);
          fetchUsers();
        }}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <Title>Users</Title>
      <div className="flex items-center justify-between pb-4 mt-2">
        <SearchBar
          onSearch={setSearchString}
          placeholderText="Search by name or email"
        />
        <PrimaryBlueButton
          prefixIcon={<IconPersonAdd />}
          label="Add User"
          onClick={() => navigate("/user/add")}
        />
      </div>
      <div className="pb-4 w-full">
        {selectedIndicies.length > 0 && (
          <BulkActionBar>
            <div>
              ({selectedIndicies.length}){" "}
              {selectedIndicies.length === 1 ? "user" : "users"} selected
            </div>
            <div className="flex items-center gap-4">
              <span>
                Max Chats
                <input
                  min={1}
                  max={16}
                  className="border rounded-md ml-2 text-black p-2 w-16"
                  type="number"
                  value={bulkMaxChats}
                  onChange={(e) => setBulkMaxChats(parseInt(e.target.value))}
                />
              </span>
              <SecondaryGrayButton onClick={bulkUpdate} label="Update" />
            </div>
          </BulkActionBar>
        )}
      </div>
      <div className="overflow-auto h-full">
        <TableBase
          columns={columns}
          data={tableData}
          onChangeSelection={setSelectedIndicies}
        />
      </div>
      <DeactivateUserModal
        show={!!deleteUserId}
        onClose={() => setDeleteUserId(undefined)}
        onDeactivate={() => handleDeactivate()}
      />
    </div>
  );
}
